import * as React from 'react';
import {useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {Routes, Route, RouteProps, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet, HelmetProvider} from 'react-helmet-async';

import {IStore} from 'redux/interface';
import {getAuthorizationAction} from 'redux/auth/authAsyncActions';
import {changeLocale} from 'redux/translation/translationAsyncActions';
import {
  firstAuthorizedRoute,
  Roles,
  RoutesUrl,
  routeTranslationKeys,
  TranslationKeys
} from 'utils/constants/routeConstants';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {SystemLanguage} from 'utils/constants/constants';

import Callback from 'pages/callback';
import Chat from 'pages/chat';
import Content from 'pages/content';
import Error from 'pages/error';
import Home from 'pages/home';
import Layout from 'components/layout';
import UserSettings from 'pages/user-settings';
import Logout from 'pages/logout';
import {MobileView} from 'components/mobile-view/MobileView';
import {Bounce, ToastContainer} from 'react-toastify';
import PublicContent from '../public-content';
import ArticlePage from '../article';
import {ArticleAuthors} from '../article-authors/ArticleAuthors';
import ArticleReferences from '../article-references';
import ArticleReference from '../article-reference';
import ArticleSection from '../article-section';
import ArticleSections from '../article-sections';
import Reviews from '../reviews';
import ReviewChat from '../review-chat';
import {AccessDenied} from '../access-dennied/AccessDenied';
import {PrivateRoute} from './PrivateRoute';
import ArticleCollections from '../article-collections';
import ArticleTables from '../article-tables';
import ArticleTableOfContents from '../article-table-of-contents';
import ArticleSummary from '../article-summary';

const App: React.FunctionComponent<RouteProps> = () => {
  const {
    translation: {
      locale,
      translations
    },
    auth: {
      userProfile
    }
  } = useSelector((state: IStore) => state);

  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const [isMobileView, setIsMobileView] = useState(false);

  useGetTranslations({
    translationKey: TranslationKeys.Common
  });
  useGetTranslations({
    translationKey: TranslationKeys.Toasts
  });
  useGetTranslations({
    translationKey: routeTranslationKeys[firstAuthorizedRoute]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, [pathname]);

  useEffect(() => {
    if (userProfile && userProfile.language !== SystemLanguage.English) {
      dispatch(changeLocale(userProfile.language));
    }
  }, [userProfile, userProfile?.language]);

  useEffect(() => {
    // if (/Android|webOS|iPhone|iPad|iPod|Mac|Macintosh|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    //   setIsMobileView(true);
    // }
    // if (/Mac|Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints === 0) { // macbooks
    //   setIsMobileView(false);
    // }
    setIsMobileView(false);
    dispatch(getAuthorizationAction());
  }, []);

  return (
    <IntlProvider locale={locale} messages={translations[locale]} onError={(e) => console.log('IntlError', e)}>
      <ToastContainer
        role="alert"
        position="bottom-left"
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        hideProgressBar
        limit={5}
        autoClose={30000}
        transition={Bounce}
      />
      <HelmetProvider>
        <Helmet>
          <html lang={locale}/>
        </Helmet>
      </HelmetProvider>

      {
        isMobileView
          ? <MobileView/>
          : <Routes>
            <Route path={RoutesUrl.Home} element={
              <Layout hideSeparator={true}>
                <Home/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Logout} element={
              <Layout>
                <Logout/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Callback} element={
              <Layout>
                <Callback/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Chat} element={
              <PrivateRoute>
                <Chat/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ChatRestart} element={
              <PrivateRoute>
                <Chat/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Settings} element={
              <PrivateRoute>
                <UserSettings/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Content} element={
              <PrivateRoute>
                <Content/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.PublicContent} element={
              <Layout>
                <PublicContent/>
              </Layout>
            }/>
            <Route path={RoutesUrl.Article} element={
              <PrivateRoute>
                <ArticlePage/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleAuthors} element={
              <PrivateRoute>
                <ArticleAuthors/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleReferences} element={
              <PrivateRoute>
                <ArticleReferences/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleReference} element={
              <PrivateRoute>
                <ArticleReference/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ReferenceAuthors} element={
              <PrivateRoute>
                <ArticleAuthors/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleSection} element={
              <PrivateRoute>
                <ArticleSection/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleSections} element={
              <PrivateRoute>
                <ArticleSections/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleCollections} element={
              <PrivateRoute>
                <ArticleCollections/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleTables} element={
              <PrivateRoute>
                <ArticleTables/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleTableOfContents} element={
              <PrivateRoute>
                <ArticleTableOfContents/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ArticleSummary} element={
              <PrivateRoute>
                <ArticleSummary/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Reviews} element={
              <PrivateRoute>
                <Reviews/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Review} element={
              <PrivateRoute>
                <ReviewChat/>
              </PrivateRoute>
            }/>
            <Route path={'*'} element={
              <Layout>
                <Error/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.AccessDenied} element={
              <Layout>
                <AccessDenied/>
              </Layout>
            }>
            </Route>
          </Routes>
      }
    </IntlProvider>
  );
};

export default App;
